import { useState, useRef, useEffect } from 'react';
import { getStorageValue, setStorageValue } from '@portal-internet/core';
import { getData } from '@portal-internet/bff';
import { useUser } from '../hooks/useUser';
import { useRouter } from 'next/router';
import useIsServiceEnabled from '../hooks/useIsServiceEnabled';
import __get from 'lodash/get';
import __isEmpty from 'lodash/isEmpty';

const expiresInMinutes = 5;
const minutesPerDay = 1440;
const expires = expiresInMinutes / minutesPerDay;
const urlBase = 'https://api.3cat.cat';

export const useItemsLaMevaLlista = () => {
    const { isAuthenticated, accessToken, id: userId } = useUser();
    const router = useRouter();
    const isVrEnabled = useIsServiceEnabled('videos-recomanats');
    const isRmEnabled = useIsServiceEnabled('resume-media');
    const isSpmEnabled = isVrEnabled && isRmEnabled;

    const [itemsLaMevaLlista, setItemsLaMevaLlista] = useState([{}]);
    const itemsLaMevaLlistaCloneRef = useRef([{}]);
    const [lastFetchedTimestamp, setLastFetchedTimestamp] = useState(0);
    const [loaded, setLoaded] = useState(false);

    const RESPOSTA_STATUS = 'resposta.status';

    const isStorageAvailable = () => {
        return !!globalThis?.window?.CCMAStorage;
    };

    const shouldProcess = () => {
        return isAuthenticated && isStorageAvailable() && !!getResolvedUserId();
    };

    const shouldFetch = () => {
        const itemsNotLoaded = __isEmpty(itemsLaMevaLlista);
        const itemsExpired = Date.now() - lastFetchedTimestamp > expiresInMinutes * 60000;
        return itemsExpired || itemsNotLoaded;
    };

    const getResolvedUserId = () => {
        return userId ?? globalThis?.window?.getUser?.()?.idint ?? globalThis?.getUser?.()?.idint ?? null;
    };

    const getEncodedUserId = () => {
        const resolvedUserId = getResolvedUserId();

        if (!resolvedUserId) {
            return null;
        }

        const rawUserId = String(resolvedUserId);

        if (globalThis?.Base64?.encode) {
            return globalThis.Base64.encode(rawUserId);
        }

        if (typeof btoa === 'function') {
            return btoa(rawUserId);
        }

        return Buffer.from(rawUserId, 'utf8').toString('base64');
    };

    const getItemsFromCCMAStorage = async () => {
        const encodedUserId = getEncodedUserId();
        if (!encodedUserId) {
            return null;
        }

        return await getStorageValue(['_ccma_lamevallista_3catinfo_', encodedUserId].join(''));
    };

    const isStatusOk = (rawData) => {
        return __get(rawData, RESPOSTA_STATUS) === 'OK';
    };

    const getItemsFromApi = async () => {
        if (!isAuthenticated || !accessToken || !isSpmEnabled) return {};
        const url =
            `${urlBase}/usuaris/llistes/obtenircontinguts?_format=json&tipus_llista=permestard` +
            `&tipologia=NOT_NOTICIA,NOT_AUTOR,DTY_CATEGORY&cache=30&vista=HISTORIC&usuari_id=${accessToken}`;
        const rawData = await getData({ queryKey: ['itemsLaMevaLlista', { url, fetchFromOrigin: true }] });
        let items = {};
        if (isStatusOk(rawData)) {
            items = rawData.resposta?.items?.item?.reduce((list, item) => {
                if (!list[item.tipologia]) {
                    list[item.tipologia] = [];
                }
                list[item.tipologia].unshift(Number(item.contingut_id));
                return list;
            }, {});

            items = {
                NOT_NOTICIA: items.NOT_NOTICIA || [],
                DTY_CATEGORY: items.DTY_CATEGORY || [],
                NOT_AUTOR: items.NOT_AUTOR || []
            };

            setItemsToCCMAStorage(items);
        }
        return items;
    };

    const setItemsToCCMAStorage = (items) => {
        const encodedUserId = getEncodedUserId();
        if (!encodedUserId) {
            return null;
        }

        const plainData = JSON.stringify(items);
        setStorageValue(['_ccma_lamevallista_3catinfo_', encodedUserId].join(''), plainData, {
            path: '/',
            domain: '.3cat.cat',
            expires: expires
        });
    };

    const obtenirItemsLaMevaLlista = async () => {
        if (!shouldProcess()) {
            setLoaded(true);
            return itemsLaMevaLlista;
        }

        if (!shouldFetch()) {
            setLoaded(true);
            return itemsLaMevaLlista;
        }

        setLoaded(false);

        let items = {};
        const plainData = await getItemsFromCCMAStorage();
        try {
            items = JSON.parse(plainData);
        } catch (e) {
            console.warn("No s'han pogut parsejar els ítems de La Meva Llista des de storage", e);
            items = {};
        }

        if (__isEmpty(items)) {
            items = await getItemsFromApi();
        }

        setItemsLaMevaLlista(items);
        itemsLaMevaLlistaCloneRef.current = structuredClone(items);
        setLoaded(true);
        setLastFetchedTimestamp(Date.now());

        return items;
    };

    useEffect(() => {
        if (isAuthenticated && accessToken && isSpmEnabled && getResolvedUserId()) {
            obtenirItemsLaMevaLlista();
        }
    }, [isAuthenticated, accessToken, isSpmEnabled, router.asPath, userId]);

    const isALaMevaLlista = (idint, tipologia) => {
        return itemsLaMevaLlista?.[tipologia]?.includes(Number(idint));
    };

    const removeItemToAPI = async (id, tipologia) => {
        if (!isAuthenticated || !accessToken || !isSpmEnabled) return false;
        const url = `${urlBase}/usuaris/llistes/esborrarcontingut?tipus_llista=permestard&tipologia=${tipologia}&contingut_id=${id}&usuari_id=${accessToken}`;
        const rawData = await getData({ queryKey: ['itemsLaMevaLlista-remove', { url, fetchFromOrigin: true }] });
        return isStatusOk(rawData);
    };

    const addItemToAPI = async (id, tipologia) => {
        if (!isAuthenticated || !accessToken || !isSpmEnabled) return false;
        const url = `${urlBase}/usuaris/llistes/afegircontingut?tipus_llista=permestard&tipologia=${tipologia}&contingut_id=${id}&usuari_id=${accessToken}`;
        const rawData = await getData({ queryKey: ['itemsLaMevaLlista-add', { url, fetchFromOrigin: true }] });
        return isStatusOk(rawData);
    };

    const eliminaALaMevaLlista = async (idint, tipologia) => {
        let isStatusOk = false;
        const items = itemsLaMevaLlista;
        if (items[tipologia]) {
            const itemId = typeof idint === 'number' ? Number(idint) : idint;
            const index = items[tipologia].indexOf(itemId);
            if (index > -1) {
                items[tipologia].splice(index, 1);
                updateItemsLaMevaLlista(items);
                isStatusOk = removeItemToAPI(idint, tipologia);
            }
        }
        isStatusOk && (itemsLaMevaLlistaCloneRef.current = structuredClone(items));
        return isStatusOk;
    };

    const afegeixALaMevaLlista = async (idint, tipologia) => {
        let isStatusOk = false;
        let items = itemsLaMevaLlista;
        if (!items[tipologia]) {
            items[tipologia] = [];
        }
        if (!isALaMevaLlista(idint, tipologia)) {
            const itemId = typeof idint === 'number' ? Number(idint) : idint;
            items[tipologia].push(itemId);
            updateItemsLaMevaLlista(items);
            isStatusOk = addItemToAPI(idint, tipologia);
        }

        isStatusOk && (itemsLaMevaLlistaCloneRef.current = structuredClone(items));
        return isStatusOk;
    };

    const updateItemsLaMevaLlista = (items) => {
        setItemsToCCMAStorage(items);
        setItemsLaMevaLlista(items);
    };

    const toggleSelection = async (id, tipologia) => {
        let result = false;

        // Clonar l'estat actual
        const newItems = { ...itemsLaMevaLlista };

        if (!newItems[tipologia]) {
            newItems[tipologia] = [];
        }

        const index = newItems[tipologia].indexOf(id);
        if (index === -1) {
            newItems[tipologia] = [...newItems[tipologia], id];
            result = await afegeixALaMevaLlista(id, tipologia);
        } else {
            newItems[tipologia] = newItems[tipologia].filter((itemId) => itemId !== id);
            result = await eliminaALaMevaLlista(id, tipologia);
        }

        setItemsLaMevaLlista(newItems);

        return result;
    };

    return {
        itemsLaMevaLlista,
        setItemsLaMevaLlista,
        toggleSelection,
        itemsLaMevaLlistaCloneRef,
        loaded,
        obtenirItemsLaMevaLlista,
        updateItemsLaMevaLlista,
        isALaMevaLlista,
        removeItemToAPI,
        addItemToAPI,
        setItemsToCCMAStorage,
        isStatusOk,
        isAuthenticated,
        accessToken,
        isSpmEnabled,
        urlBase
    };
};
